import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useHasPermission } from '@/hooks/usePermissions';
import { useMyMemberships } from '@/hooks/useClinics';
import { pickPostAuthDestination } from '@/hooks/usePostAuthRoute';
import { Redirect } from '@/routing/Redirect';
import { CLINIC_PICKER_PATH, TENANT } from '@/routing/tenant';
import type { PermissionKey } from '@/lib/permissions';

interface RequireAuthProps {
  children: React.ReactNode;
}

/**
 * Gate for the main app shell: requires a valid session AND membership of the
 * clinic this URL names.
 *
 * The URL's clinic IS the tenant — the API scopes every request by the slug in
 * the path (`/api/c/<slug>/...`) and checks membership itself. So this guard is
 * not reconciling two sources of truth; it is deciding what to render for
 * someone standing at a clinic that is not theirs, which the API would refuse
 * anyway. None of the ways to arrive here is an attack: a stale bookmark, a
 * renamed slug, a link pasted between two clinics' staff.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, memberships } = useAuth();
  const location = useLocation();
  // Refresh memberships on every protected-shell mount so a stale persisted
  // session (user removed from clinic on another device, role changed, etc.)
  // is caught at the boundary. The hook is gated on isAuthenticated to avoid
  // an unauthenticated request from the public surface.
  const membershipsQuery = useMyMemberships({ enabled: isAuthenticated });

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // `memberships` is not persisted across reloads (see authStore partialize),
  // so on refresh it hydrates as []. Deciding before the query settles would
  // read every reload as "you belong to no clinic" and bounce the user out of
  // /add or /issue to the picker. Wait for the fetch to resolve.
  const membershipsReady = memberships.length > 0 || membershipsQuery.isFetched;
  if (!membershipsReady) {
    return null;
  }

  // Is the clinic this URL names one of theirs? If not — no memberships at all,
  // a stale bookmark, a renamed slug, a link pasted between two clinics' staff —
  // the answer is the same question the post-auth router already answers, and it
  // may cross the router's basename, hence `Redirect` rather than `<Navigate>`.
  //
  // A membership to a clinic a platform operator has switched off counts as
  // "not here": the API refuses every request inside it with CLINIC_DISABLED,
  // so rendering the shell would only produce a screen of failed panels. The
  // picker is where that clinic is still listed, badged, with the explanation.
  const here = memberships.find((m) => m.slug === TENANT.slug);
  const inThisClinic = TENANT.mode === 'tenant' && here !== undefined && !here.disabled;
  if (!inThisClinic) {
    return (
      <Redirect
        to={pickPostAuthDestination(memberships, `${location.pathname}${location.search}`)}
      />
    );
  }
  return <>{children}</>;
}

/**
 * Gate for the global pages that need a session and nothing else: the clinic
 * picker and the onboarding wizard.
 *
 * It deliberately does NOT bounce a user who already has clinics. It used to —
 * "you have a clinic, you cannot be onboarding" — which was right when a user
 * could hold only one. Now that they can hold several, creating another is an
 * ordinary thing to do, and that rule made `/create-dispensary` unreachable for
 * exactly the people the picker offers it to.
 *
 * No loop results: `RequireAuth` never sends anyone here directly, and the
 * picker only forwards to the wizard when there is genuinely nothing to pick.
 */
export function RequireSession({ children }: RequireAuthProps) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

/**
 * Gate for `/admin` — the platform surface.
 *
 * Deliberately NOT a `PermissionKey` check: the permission catalogue is
 * per-clinic, and this privilege sits above the tenant boundary. It reads
 * `user.isSuperAdmin` from the session, which gates rendering only — every
 * `/api/admin/*` call is checked against the database independently, so this
 * guard's job is to avoid drawing a dashboard nobody can use, not to enforce
 * anything.
 *
 * Redirects rather than showing an error, matching `RequirePermission`: someone
 * arriving here without the flag has followed a stale link or a shared URL, not
 * mounted an attack.
 */
export function RequireSuperAdmin({ children }: RequireAuthProps) {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (!user?.isSuperAdmin) {
    return <Navigate to={CLINIC_PICKER_PATH} replace />;
  }
  return <>{children}</>;
}

interface RequirePermissionProps {
  permission: PermissionKey;
  children: React.ReactNode;
}

/**
 * Route-level gate on a single permission.
 *
 * Replaces the old exact-role match, which could only ever ask "are you the
 * admin?". Redirects home rather than showing an error: a user who cannot open
 * a page usually got here from a stale link or a bookmark, not an attack, and
 * the API refuses independently either way.
 */
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const { user } = useAuth();
  const allowed = useHasPermission(permission);
  if (!user) return <Navigate to="/login" replace />;
  if (!allowed) return <Navigate to="/" replace />;
  return <>{children}</>;
}
